import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import Login from '../views/Login.vue'
import UserProfile from '../views/UserProfile.vue'
import ActiveUsers from '../views/ActiveUsers.vue'
import Spark from '../views/Spark.vue'
import MyLoves from '../views/MyLoves.vue'
import { useUserStore } from '@/stores/user'
import { supabase } from '@/lib/supabase'
import MemoryGame from '../components/MemoryGame.vue'
import ExtraView from '../views/ExtraView.vue'
import PrivacyPolicy from '../views/PrivacyPolicy.vue' // Import PrivacyPolicy
import SevenFacts from '../views/SevenFacts.vue'
import AdminView from '../views/AdminView.vue' // Import AdminView
import DuoNamo from '../views/DuoNamo.vue' // Import DuoNamo
import PersonalsList from '../views/PersonalsList.vue'
import PersonalDetail from '../views/PersonalDetail.vue'
import CreatePersonal from '../views/CreatePersonal.vue'
import EditPersonal from '../views/EditPersonal.vue'
import MyPersonals from '../views/MyPersonals.vue'

async function redirectToOwnProfile(to, from, next, query = {}) {
  const devAuthBypass =
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' ||
    localStorage.getItem('dev-auth-bypass') === 'true'
  const testUserId = import.meta.env.VITE_DEV_TEST_USER_ID || '369c76b4-fa01-4e29-b87c-cbfe879c1989'

  if (devAuthBypass) {
    next({ name: 'user-profile', params: { id: testUserId }, query })
    return
  }

  const {
    data: { user }
  } = await supabase.auth.getUser()

  if (user) {
    next({ name: 'user-profile', params: { id: user.id }, query })
  } else {
    next({ name: 'login' })
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: { requiresProfile: true }
    },
    {
      path: '/login',
      name: 'login',
      component: Login
    },
    {
      path: '/profile',
      name: 'profile',
      beforeEnter: redirectToOwnProfile,
      meta: { requiresProfile: true }
    },
    {
      path: '/profile/edit',
      name: 'profile-edit',
      beforeEnter: (to, from, next) => redirectToOwnProfile(to, from, next, { mode: 'edit' }),
      meta: { requiresProfile: true }
    },
    {
      path: '/user/:id',
      name: 'user-profile',
      component: UserProfile,
      meta: { requiresProfile: true }
    },
    {
      path: '/exploring',
      name: 'exploring',
      component: ActiveUsers,
      meta: { requiresProfile: true }
    },
    {
      path: '/active',
      redirect: '/exploring'
    },
    {
      path: '/everyone',
      redirect: '/exploring'
    },
    {
      path: '/more',
      name: 'more',
      component: ExtraView,
      meta: { requiresProfile: true }
    },
    {
      path: '/extra',
      redirect: '/more'
    },
    {
      path: '/spark',
      name: 'spark',
      component: Spark,
      meta: { requiresProfile: true }
    },
    {
      path: '/my-loves',
      name: 'my-loves',
      component: MyLoves,
      meta: { requiresProfile: true }
    },
    {
      path: '/memory-game',
      name: 'memory-game',
      component: MemoryGame,
      meta: { requiresProfile: true }
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: PrivacyPolicy
    },
    {
      path: '/seven-facts',
      name: 'seven-facts',
      component: SevenFacts,
      meta: { requiresProfile: true }
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminView,
      meta: { requiresProfile: true, requiresAdmin: true }
    },
    {
      path: '/duonamo',
      name: 'duonamo',
      component: DuoNamo,
      meta: { requiresProfile: true }
    },
    // Personals routes
    {
      path: '/personals',
      name: 'personals',
      component: PersonalsList,
      meta: { requiresProfile: true }
    },
    {
      path: '/personals/new',
      name: 'create-personal',
      component: CreatePersonal,
      meta: { requiresProfile: true }
    },
    {
      path: '/personals/:id',
      name: 'personal-detail',
      component: PersonalDetail,
      meta: { requiresProfile: true }
    },
    {
      path: '/personals/:id/edit',
      name: 'edit-personal',
      component: EditPersonal,
      meta: { requiresProfile: true }
    },
    {
      path: '/my/personals',
      name: 'my-personals',
      component: MyPersonals,
      meta: { requiresProfile: true }
    },
    // Reply management routes (require authenticated profile)
    {
      path: '/my/personals/:id/replies',
      name: 'ad-replies',
      // Lazy-load the view to keep initial bundle light; create file at src/views/AdReplies.vue
      component: () => import('../views/AdReplies.vue'),
      meta: { requiresProfile: true }
    },
    {
      path: '/my/replies',
      name: 'my-replies',
      // Lazy-load the view; create file at src/views/MyReplies.vue
      component: () => import('../views/MyReplies.vue'),
      meta: { requiresProfile: true }
    },
  ]
})

router.beforeEach(async (to, from) => {
  const userStore = useUserStore()

  // Check if the route requires a complete profile
  if (to.meta.requiresProfile) {
    // Development auth bypass for testing
    const devAuthBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' || 
                         localStorage.getItem('dev-auth-bypass') === 'true'
    const testUserId = import.meta.env.VITE_DEV_TEST_USER_ID || '369c76b4-fa01-4e29-b87c-cbfe879c1989'
    
    let user = null
    
    if (devAuthBypass) {
      user = {
        id: testUserId,
        email: 'fraiponts@gmail.com',
        user_metadata: {
          name: 'Test Hans',
          full_name: 'Test Hans'
        }
      }
    } else {
      // First check authentication normally
      const {
        data: { user: authUser }
      } = await supabase.auth.getUser()
      user = authUser
    }
    
    if (!user) {
      return { name: 'login' }
    }

    // Then check profile
    if (!userStore.profile) {
      try {
        await userStore.fetchProfile(user.id)
      } catch (error) {
        console.error('Error fetching profile:', error)
      }
    }

    // If profile is incomplete or onboarding is not completed, redirect to user profile in edit mode
    if (!userStore.profile || !userStore.profile.name || !userStore.profile.onboarding_complete) {
      // If the user is already on the user-profile edit page, allow it
      if (to.name === 'user-profile' && to.query.mode === 'edit' && to.params.id === user.id) {
        return true
      }
      return { name: 'user-profile', params: { id: user.id }, query: { mode: 'edit' } }
    }

    // Allow users to edit their own profiles even after onboarding is complete
    // (This logic was previously blocking legitimate profile editing)
  }

  // Check admin requirement
  if (to.meta.requiresAdmin) {
    if (!userStore.profile?.is_admin) {
      // Redirect non-admins to home page
      return { name: 'home' }
    }
  }

  return true
})

export default router
